import React from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { CheckCircle } from "lucide-react";

const Api = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  const featureVariants = {
    hidden: { x: -20, opacity: 0 },
    visible: {
      x: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
  };

  const cardVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: (i: number) => ({
      y: 0,
      opacity: 1,
      transition: {
        delay: 0.3 + i * 0.1,
        duration: 0.6,
        ease: "easeOut",
      },
    }),
  };

  return (
    <motion.div
      ref={ref}
      className="w-full py-16 px-4 md:px-8 lg:px-16 bg-white overflow-hidden"
      variants={containerVariants}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
    >
      <div className="container mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <motion.div variants={itemVariants} className="space-y-6">
          <motion.h2
            variants={itemVariants}
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-red-900 leading-tight"
          >
            Shape the payment experience your way using our easy to integrate
            APIs
          </motion.h2>

          <motion.p
            variants={itemVariants}
            className="text-lg text-red-700 max-w-xl"
          >
            Developers rely on our clear, comprehensive APIs to build everything
            from quick weekend prototypes to powerful financial products that
            scale to hundreds of thousands of users. If you can dream it, you
            can build it with Vaspay{" "}
          </motion.p>

          <motion.div variants={itemVariants} className="space-y-4 pt-4">
            <motion.div
              variants={featureVariants}
              className="flex items-start gap-3"
            >
              <CheckCircle className="h-6 w-6 text-red-500 flex-shrink-0 mt-0.5" />
              <span className="text-slate-800">
               Enable fast, flexible billing—whether it's one-time or ongoing on your website or app.
              </span>
            </motion.div>

            <motion.div
              variants={featureVariants}
              className="flex items-start gap-3"
            >
              <CheckCircle className="h-6 w-6 text-red-500 flex-shrink-0 mt-0.5" />
              <span className="text-slate-800">Make instant transfers</span>
            </motion.div>

            <motion.div
              variants={featureVariants}
              className="flex items-start gap-3"
            >
              <CheckCircle className="h-6 w-6 text-red-500 flex-shrink-0 mt-0.5" />
              <span className="text-slate-800">
                Retrieve all your transaction and customer data
              </span>
            </motion.div>

            <motion.div
              variants={featureVariants}
              className="flex items-start gap-3"
            >
              <CheckCircle className="h-6 w-6 text-red-500 flex-shrink-0 mt-0.5" />
              <span className="text-slate-800">
                Verify the identity of customers
              </span>
            </motion.div>
          </motion.div>
        </motion.div>

        <motion.div
          variants={itemVariants}
          className="relative h-[300px] md:h-[400px] w-full"
        >
          {/* Cards illustration */}
          <motion.div
            custom={0}
            variants={cardVariants}
            className="absolute top-[10%] right-[10%] w-[140px] h-[200px] md:w-[180px] md:h-[240px] bg-slate-800 rounded-lg shadow-lg transform rotate-6 border-t-4 border-cyan-400"
          />
          <motion.div
            custom={1}
            variants={cardVariants}
            className="absolute top-[20%] right-[30%] w-[140px] h-[200px] md:w-[180px] md:h-[240px] bg-sky-100 rounded-lg shadow-lg transform -rotate-3 border-t-4 border-sky-400"
          />
          <motion.div
            custom={2}
            variants={cardVariants}
            className="absolute top-[40%] right-[20%] w-[140px] h-[200px] md:w-[180px] md:h-[240px] bg-amber-100 rounded-lg shadow-lg transform rotate-12 border-t-4 border-amber-400"
          />
          <motion.div
            custom={3}
            variants={cardVariants}
            className="absolute bottom-[10%] left-[10%] md:left-[20%] w-[120px] h-[180px] md:w-[160px] md:h-[220px] bg-emerald-100 rounded-lg shadow-lg transform -rotate-6 border-t-4 border-emerald-400"
          />
          <motion.div
            custom={4}
            variants={cardVariants}
            className="absolute bottom-[20%] right-[5%] w-[120px] h-[180px] md:w-[160px] md:h-[220px] bg-blue-100 rounded-lg shadow-lg transform rotate-3 border-t-4 border-blue-400"
          />
        </motion.div>
      </div>
    </motion.div>
  );
};

export default Api;
